/**
 * Utilitaires pour la détection et gestion des commandes avec livraison
 * Créé le 21 juillet 2026
 */

/**
 * Détecte si une réservation est une commande avec livraison
 * @param {Object} reservation - Objet réservation
 * @returns {boolean}
 */
export const isDeliveryOrder = (reservation) => {
  if (!reservation || !reservation.extra_fees) {
    return false;
  }

  const deliveryType = reservation.extra_fees.delivery_type;
  return deliveryType === 'express' || deliveryType === 'standard';
};

/**
 * Vérifie si une commande livraison a été expédiée
 * @param {Object} reservation - Objet réservation
 * @returns {boolean}
 */
export const isDeliveryShipped = (reservation) => {
  return isDeliveryOrder(reservation) && reservation.pickup_confirmed === true;
};

/**
 * Vérifie si une commande livraison est complète (simulée)
 * @param {Object} reservation - Objet réservation
 * @returns {boolean}
 */
export const isDeliveryCompleted = (reservation) => {
  return isDeliveryOrder(reservation) && reservation.return_confirmed === true;
};

/**
 * Récupère le type de livraison (express ou standard)
 * @param {Object} reservation - Objet réservation
 * @returns {string|null}
 */
export const getDeliveryType = (reservation) => {
  if (!isDeliveryOrder(reservation)) {
    return null;
  }
  return reservation.extra_fees.delivery_type;
};

/**
 * Récupère l'adresse de livraison
 * @param {Object} reservation - Objet réservation
 * @returns {Object|null}
 */
export const getDeliveryAddress = (reservation) => {
  if (!isDeliveryOrder(reservation)) {
    return null;
  }
  return reservation.extra_fees.delivery_address || null;
};

/**
 * Formate l'adresse de livraison en string
 * @param {Object} reservation - Objet réservation
 * @returns {string|null}
 */
export const formatDeliveryAddress = (reservation) => {
  const address = getDeliveryAddress(reservation);
  if (!address) return null;

  const parts = [
    address.street,
    address.postalCode,
    address.city,
    address.country
  ].filter(Boolean);

  return parts.join(', ');
};

/**
 * Récupère les infos de tracking depuis owner_notes
 * @param {Object} reservation - Objet réservation
 * @returns {Object|null}
 */
export const getTrackingInfo = (reservation) => {
  if (!isDeliveryOrder(reservation)) {
    return null;
  }

  const ownerNotes = reservation.owner_notes || {};
  
  if (!ownerNotes.tracking_number) {
    return null;
  }

  return {
    tracking_number: ownerNotes.tracking_number,
    carrier: ownerNotes.carrier,
    shipped_at: ownerNotes.shipped_at,
    estimated_delivery: ownerNotes.estimated_delivery,
    notes: ownerNotes.notes
  };
};

/**
 * Vérifie si l'utilisateur peut confirmer l'expédition
 * @param {Object} reservation - Objet réservation
 * @param {string} userId - ID de l'utilisateur courant
 * @returns {boolean}
 */
export const canConfirmShipment = (reservation, userId) => {
  if (!reservation || !userId) {
    return false;
  }
  
  // Doit être une commande avec livraison
  if (!isDeliveryOrder(reservation)) {
    return false;
  }
  
  // Vérifier si l'utilisateur est le owner (plusieurs formats possibles)
  const listing = reservation.listing || reservation.Listing || {};
  
  // Chercher owner_id dans TOUS les formats possibles
  const ownerId = listing.owner_id       // snake_case direct
                || listing.ownerId         // camelCase direct
                || listing.User?.id        // via User
                || listing.owner?.id       // via owner
                || listing.Owner?.id;      // via Owner
  
  if (!ownerId || ownerId !== userId) {
    return false;
  }
  
  // Doit être confirmée et pas encore expédiée
  return reservation.status === 'confirmed' && !reservation.pickup_confirmed;
};

export default {
  isDeliveryOrder,
  isDeliveryShipped,
  isDeliveryCompleted,
  getDeliveryType,
  getDeliveryAddress,
  formatDeliveryAddress,
  getTrackingInfo,
  canConfirmShipment
};
